// src/components/admin/holidays/HolidayManagementPage.js

/**
 * Holiday Management Page
 * Main UI component for managing company holidays
 * Port of HolidayManagementView.swift for React web application
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { HolidayProvider, useHolidayContext } from '../../../contexts/HolidayContext';
import { Holiday } from '../../../models/HolidayModels';

// MARK: - Delete Confirmation Modal
const DeleteConfirmModal = ({ isOpen, holidayName, onConfirm, onCancel }) => {
  const { t } = useTranslation('settings');

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div
        className="modal-content delete-confirm-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>{t('holidayManagement.confirmations.deleteTitle')}</h2>
          <button
            className="close-button"
            onClick={onCancel}
            aria-label={t('holidayManagement.actions.close')}
          >
            ×
          </button>
        </div>

        <div className="delete-confirm-body">
          <p className="delete-confirm-question">
            {t('holidayManagement.confirmations.deleteQuestion')}
          </p>
          <p className="delete-confirm-name">"{holidayName}"</p>
          <p className="delete-confirm-warning">
            {t('holidayManagement.confirmations.deleteWarning')}
          </p>

          <div className="modal-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={onCancel}
            >
              {t('holidayManagement.actions.cancel')}
            </button>
            <button
              type="button"
              className="delete-confirm-button"
              onClick={onConfirm}
            >
              {t('holidayManagement.actions.delete')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// MARK: - Holiday Row Component
const HolidayRow = ({ holiday, onEdit, onDelete }) => {
  const { t } = useTranslation('settings');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDeleteConfirmed = async () => {
    setIsDeleting(true);
    setShowDeleteConfirm(false);
    await onDelete(holiday.id);
    setIsDeleting(false);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('de-DE', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <>
      <div className="holiday-row" onClick={() => onEdit(holiday)}>
        <div className="holiday-info">
          <div className="holiday-name">{holiday.name}</div>
          <div className="holiday-date">{formatDate(holiday.date)}</div>
        </div>

        <div className="holiday-actions">
          <span className={`holiday-badge ${holiday.isPaidHoliday ? 'paid' : 'unpaid'}`}>
            {holiday.isPaidHoliday
              ? t('holidayManagement.status.paid')
              : t('holidayManagement.status.unpaid')}
          </span>

          <button
            className="delete-button"
            onClick={(e) => {
              e.stopPropagation();
              setShowDeleteConfirm(true);
            }}
            disabled={isDeleting}
            aria-label={t('holidayManagement.actions.deleteHoliday')}
          >
            {isDeleting ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12,6 12,12 16,14"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3,6 5,6 21,6"/>
                <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"/>
                <line x1="10" y1="11" x2="10" y2="17"/>
                <line x1="14" y1="11" x2="14" y2="17"/>
              </svg>
            )}
          </button>
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={showDeleteConfirm}
        holidayName={holiday.name}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </>
  );
};

// MARK: - Add/Edit Holiday Modal
const HolidayModal = ({ isOpen, onClose, holiday, onSave }) => {
  const { t } = useTranslation('settings');
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    isPaidHoliday: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState('');

  const isEditMode = Boolean(holiday);

  useEffect(() => {
    if (isOpen) {
      if (holiday) {
        setFormData({
          name: holiday.name,
          date: Holiday.formatDateForInput(holiday.date),
          isPaidHoliday: holiday.isPaidHoliday
        });
      } else {
        const currentYear = new Date().getFullYear();
        const defaultDate = new Date(currentYear, 0, 1);
        setFormData({
          name: '',
          date: Holiday.formatDateForInput(defaultDate),
          isPaidHoliday: true
        });
      }
      setValidationError('');
    }
  }, [isOpen, holiday]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setValidationError('');
  };

  const validateForm = () => {
    const trimmedName = formData.name.trim();

    if (!trimmedName) {
      setValidationError(t('holidayManagement.validation.nameRequired'));
      return false;
    }

    if (trimmedName.length > 100) {
      setValidationError(t('holidayManagement.validation.nameTooLong'));
      return false;
    }

    if (!formData.date) {
      setValidationError(t('holidayManagement.validation.dateRequired'));
      return false;
    }

    const selectedDate = new Date(formData.date + 'T00:00:00');
    if (isNaN(selectedDate.getTime())) {
      setValidationError(t('holidayManagement.validation.invalidDate'));
      return false;
    }

    const year = selectedDate.getFullYear();
    if (year < 2000 || year > 2100) {
      setValidationError(t('holidayManagement.validation.yearRange'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const holidayData = {
        name: formData.name.trim(),
        date: new Date(formData.date + 'T00:00:00'),
        isPaidHoliday: formData.isPaidHoliday
      };

      if (isEditMode) {
        holidayData.id = holiday.id;
      }

      const success = await onSave(holidayData);

      if (success) {
        onClose();
      }
    } catch (error) {
      console.error('Error saving holiday:', error);
      setValidationError(t('holidayManagement.messages.unexpectedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditMode ? t('holidayManagement.form.editHoliday') : t('holidayManagement.form.addHoliday')}</h2>
          <button
            className="close-button"
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label={t('holidayManagement.actions.close')}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="holiday-form">
          <div className="form-group">
            <label htmlFor="holiday-name">{t('holidayManagement.form.holidayName')}</label>
            <input
              id="holiday-name"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder={t('holidayManagement.form.holidayNamePlaceholder')}
              maxLength={100}
              disabled={isSubmitting}
              autoComplete="off"
            />
          </div>

          <div className="form-group">
            <label htmlFor="holiday-date">{t('holidayManagement.form.holidayDate')}</label>
            <input
              id="holiday-date"
              type="date"
              value={formData.date}
              onChange={(e) => handleInputChange('date', e.target.value)}
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.isPaidHoliday}
                onChange={(e) => handleInputChange('isPaidHoliday', e.target.checked)}
                disabled={isSubmitting}
              />
              <span>{t('holidayManagement.form.paidHoliday')}</span>
            </label>
            <small className="help-text">
              {t('holidayManagement.form.paidHolidayHelp')}
            </small>
          </div>

          {validationError && (
            <div className="error-message">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="m21,5l-3,16l-5,-2l-5,2l-3,-16z"/>
                <circle cx="12" cy="9" r="1"/>
                <path d="M12,14 v-1"/>
              </svg>
              {validationError}
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              {t('holidayManagement.actions.cancel')}
            </button>
            <button
              type="submit"
              className="save-button"
              disabled={isSubmitting || !formData.name.trim() || !formData.date}
            >
              {isSubmitting ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12,6 12,12 16,14"/>
                  </svg>
                  {t('holidayManagement.actions.saving')}
                </>
              ) : (isEditMode ? t('holidayManagement.form.saveChanges') : t('holidayManagement.actions.addHoliday'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// MARK: - Common Holidays Modal
const CommonHolidaysModal = ({ isOpen, onClose, selectedYear, onAddCommon }) => {
  const { t } = useTranslation('settings');
  const [isAdding, setIsAdding] = useState(false);
  const [previewHolidays, setPreviewHolidays] = useState([]);

  useEffect(() => {
    if (isOpen) {
      const commonHolidays = Holiday.createCommonHolidays(selectedYear, 'de');
      setPreviewHolidays(commonHolidays);
    }
  }, [isOpen, selectedYear]);

  const handleAddCommon = async () => {
    setIsAdding(true);
    const success = await onAddCommon();
    setIsAdding(false);

    if (success) {
      onClose();
    }
  };

  const handleClose = () => {
    if (!isAdding) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('holidayManagement.commonHolidays.title')}</h2>
          <button
            className="close-button"
            onClick={handleClose}
            disabled={isAdding}
            aria-label={t('holidayManagement.actions.close')}
          >
            ×
          </button>
        </div>

        <div className="common-holidays-content">
          <p className="description">
            {t('holidayManagement.commonHolidays.description', { year: selectedYear })}
          </p>

          <div className="holidays-preview">
            <h3>{t('holidayManagement.commonHolidays.preview')}</h3>
            <div className="preview-list">
              {previewHolidays.map((holiday) => (
                <div key={holiday.id} className="preview-item">
                  <span className="preview-name">{holiday.name}</span>
                  <span className="preview-date">
                    {holiday.date.toLocaleDateString('de-DE', {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short'
                    })}
                  </span>
                  <span className={`holiday-badge ${holiday.isPaidHoliday ? 'paid' : 'unpaid'}`}>
                    {holiday.isPaidHoliday
                      ? t('holidayManagement.status.paid')
                      : t('holidayManagement.status.unpaid')}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {previewHolidays.length === 0 && (
            <div className="no-holidays-message">
              {t('holidayManagement.commonHolidays.noHolidays', { year: selectedYear })}
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={handleClose}
              disabled={isAdding}
            >
              {t('holidayManagement.actions.cancel')}
            </button>
            <button
              type="button"
              className="add-button"
              onClick={handleAddCommon}
              disabled={isAdding || previewHolidays.length === 0}
            >
              {isAdding ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12,6 12,12 16,14"/>
                  </svg>
                  {t('holidayManagement.actions.adding')}
                </>
              ) : t('holidayManagement.commonHolidays.addHolidays', { count: previewHolidays.length })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// MARK: - Main Holiday Management Component
const HolidayManagementContent = ({ onClose }) => {
  const { t } = useTranslation('settings');
  const {
    sortedHolidays,
    selectedYear,
    isLoading,
    errorMessage,
    successMessage,
    hasHolidays,
    stats,
    createHoliday,
    updateHoliday,
    deleteHoliday,
    addCommonHolidays,
    goToPreviousYear,
    goToNextYear,
    clearMessages
  } = useHolidayContext();

  const [showAddModal, setShowAddModal] = useState(false);
  const [showCommonModal, setShowCommonModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [selectedYear]);

  const handleSaveHoliday = async (holidayData) => {
    if (editingHoliday) {
      return await updateHoliday(editingHoliday.id, holidayData);
    } else {
      return await createHoliday(holidayData);
    }
  };

  const handleEditHoliday = (holiday) => {
    setEditingHoliday(holiday);
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingHoliday(null);
  };

  const handleAddCommon = async () => {
    return await addCommonHolidays('de');
  };

  return (
    <div className="holiday-management">
      {/* Header */}
      <div className="holiday-header">
        <div className="header-top">
          <button
            className="close-button"
            onClick={onClose}
            aria-label={t('holidayManagement.navigation.back')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15,18 9,12 15,6"/>
            </svg>
            {t('holidayManagement.navigation.back')}
          </button>
          <h1>{t('holidayManagement.title')}</h1>
        </div>

        {/* Year Navigation and Actions */}
        <div className="year-navigation">
          <div className="year-controls">
            <button
              className="year-nav-button"
              onClick={goToPreviousYear}
              aria-label={t('holidayManagement.navigation.previousYear')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15,18 9,12 15,6"/>
              </svg>
            </button>

            <div className="year-display">
              <span className="year-number">{selectedYear}</span>
              <span className="year-label">{t('holidayManagement.navigation.holidays')}</span>
            </div>

            <button
              className="year-nav-button"
              onClick={goToNextYear}
              aria-label={t('holidayManagement.navigation.nextYear')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9,18 15,12 9,6"/>
              </svg>
            </button>
          </div>

          <div className="action-buttons">
            <button
              className="common-holidays-button"
              onClick={() => setShowCommonModal(true)}
              disabled={isLoading}
            >
              {t('holidayManagement.actions.addCommonHolidays')}
            </button>

            <button
              className="add-holiday-button"
              onClick={() => setShowAddModal(true)}
              disabled={isLoading}
            >
              {t('holidayManagement.actions.addHoliday')}
            </button>
          </div>
        </div>

        {/* Statistics */}
        {hasHolidays && (
          <div className="holiday-stats">
            <div className="stat-item">
              <span className="stat-label">{t('holidayManagement.statistics.total')}</span>
              <span className="stat-number">{stats.totalHolidays}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">{t('holidayManagement.statistics.paid')}</span>
              <span className="stat-number">{stats.paidHolidays}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">{t('holidayManagement.statistics.unpaid')}</span>
              <span className="stat-number">{stats.unpaidHolidays}</span>
            </div>
          </div>
        )}
      </div>

      {/* Messages */}
      {errorMessage && (
        <div className="message error-message">
          <span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="m21,5l-3,16l-5,-2l-5,2l-3,-16z"/>
              <circle cx="12" cy="9" r="1"/>
              <path d="M12,14 v-1"/>
            </svg>
            {errorMessage}
          </span>
          <button className="dismiss-button" onClick={clearMessages}>×</button>
        </div>
      )}

      {successMessage && (
        <div className="message success-message">
          <span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20,6 9,17 4,12"/>
            </svg>
            {successMessage}
          </span>
          <button className="dismiss-button" onClick={clearMessages}>×</button>
        </div>
      )}

      {/* Content */}
      <div className="holiday-content">
        {isLoading && (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>{t('holidayManagement.states.loading')}</p>
          </div>
        )}

        {!isLoading && !hasHolidays && (
          <div className="empty-state">
            <div className="empty-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
            </div>
            <h3>{t('holidayManagement.states.empty.title', { year: selectedYear })}</h3>
            <p>{t('holidayManagement.states.empty.description')}</p>
          </div>
        )}

        {!isLoading && hasHolidays && (
          <div className="holidays-list">
            {sortedHolidays.map((holiday) => (
              <HolidayRow
                key={holiday.id}
                holiday={holiday}
                onEdit={handleEditHoliday}
                onDelete={deleteHoliday}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <HolidayModal
        isOpen={showAddModal}
        onClose={handleCloseModal}
        holiday={editingHoliday}
        onSave={handleSaveHoliday}
      />

      <CommonHolidaysModal
        isOpen={showCommonModal}
        onClose={() => setShowCommonModal(false)}
        selectedYear={selectedYear}
        onAddCommon={handleAddCommon}
      />
    </div>
  );
};

// MARK: - Main Export Component with Provider
const HolidayManagementPage = ({ companyId, onClose }) => {
  const { t } = useTranslation('settings');

  if (!companyId) {
    return (
      <div className="holiday-management error">
        <div className="error-message">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="m21,5l-3,16l-5,-2l-5,2l-3,-16z"/>
            <circle cx="12" cy="9" r="1"/>
            <path d="M12,14 v-1"/>
          </svg>
          {t('holidayManagement.validation.companyRequired')}
        </div>
      </div>
    );
  }

  return (
    <HolidayProvider companyId={companyId}>
      <HolidayManagementContent onClose={onClose} />
    </HolidayProvider>
  );
};

export default HolidayManagementPage;